import React, { useState, useRef, useEffect } from 'react';
import { useCCContext } from '../CC/CCContext';
import styles from './User.module.css';
import GenericIconButton from '../GenericComponents/GenericIconButton';
import genericStyles from '../GenericComponents/GenericStyles.module.css';
import AlbumEditor from '../AlbumEditor/AlbumEditor';
import UserManagement from './UserManagement';
import CreateAccount from './CreateAccount';

function User() {
    const { user, setUser } = useCCContext();
    const [showLoginModal, setShowLoginModal] = useState(false);
    const [showCreateAccountModal, setShowCreateAccountModal] = useState(false);
    const [showAlbumEditorModal, setShowAlbumEditorModal] = useState(false);
    const [showUserManagementModal, setShowUserManagementModal] = useState(false);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const menuButtonRef = useRef<HTMLButtonElement | null>(null);
    const [showMenuDropdown, setShowMenuDropdown] = useState(false);
    const [message, setMessage] = useState<string | null>(null);
    const [messageVisible, setMessageVisible] = useState(false);
    const [loginError, setLoginError] = useState<string | null>(null);
    const warningTimerRef = useRef<number | null>(null);
    const logoutTimerRef = useRef<number | null>(null);

    useEffect(() => {
        return () => {
            if (warningTimerRef.current) {
                clearTimeout(warningTimerRef.current);
            }
            if (logoutTimerRef.current) {
                clearTimeout(logoutTimerRef.current);
            }
        };
    }, []);

    const handleLogin = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setLoginError(null);
        try {
            const response = await fetch(
                `${process.env.REACT_APP_USERMANAGEMENT_URL}?action=login&email=${encodeURIComponent(email)}&password=${encodeURIComponent(password)}`
            );
            const respText = await response.text();
            if (respText === "Invalid Credentials") {
                setLoginError("Invalid email or password. Please try again.");
            } else {
                const data = JSON.parse(respText);
                console.log('Login response:', data);
                if (response.ok && data) {
                    setUser(data);
                    setShowLoginModal(false);
                } else {
                    if (data.message === "Invalid Credentials") {
                        setLoginError("Invalid email or password. Please try again.");
                    } else {
                        setLoginError("An unexpected error occurred. Please try again later.");
                    }
                }
            }
        } catch (error) {
            console.error('Login error:', error);
            setLoginError("Failed to connect to the server. Please check your internet connection and try again.");
        }
    };

    useEffect(() => {
        if (user?.tokenExpires) {
            const expirationTime = new Date(user.tokenExpires).getTime();
            const currentTime = new Date().getTime();
            const timeRemaining = expirationTime - currentTime;

            if (warningTimerRef.current) {
                clearTimeout(warningTimerRef.current);
            }
            if (logoutTimerRef.current) {
                clearTimeout(logoutTimerRef.current);
            }

            warningTimerRef.current = setTimeout(() => {
                setMessage('You will be logged out in 1 minute');
                setMessageVisible(true);

                setTimeout(() => {
                    setMessageVisible(false);
                }, 3000);

            }, timeRemaining - 60000) as unknown as number;

            logoutTimerRef.current = setTimeout(() => {
                setUser(null);
                setShowMenuDropdown(false);
                setMessage('You have been logged out');
                setMessageVisible(true);

                setTimeout(() => {
                    setMessageVisible(false);
                }, 3000);
            }, timeRemaining) as unknown as number;
        }
    }, [user?.tokenExpires]);

    const renderUserMenu = () => {
        let x = 0;
        let y = 0;

        if (menuButtonRef.current) {
            const rect = menuButtonRef.current.getBoundingClientRect();

            const parentModal = menuButtonRef.current.parentElement;
            if (parentModal) {
                const modalRect = parentModal.getBoundingClientRect();
                x = rect.left - modalRect.left - 108;
                y = rect.bottom + 2 - modalRect.top + 12;
            }
        }

        const menuItems = [
            {
                label: 'edit', onClick: () => {
                    console.log('Edit clicked');
                    setShowMenuDropdown(false);
                }
            },

            {
                label: 'album editor', onClick: () => {
                    setShowAlbumEditorModal(true);
                    setShowMenuDropdown(false);
                }
            },

            {
                label: 'logout', onClick: () => {
                    setUser(null);
                    setShowMenuDropdown(false);
                }
            }
        ];

        if (user?.userDetails?.roles?.includes('admin')) {
            menuItems.splice(1, 0, {
                label: 'manage users', onClick: () => {
                    console.log('Manage Users clicked');
                    setShowUserManagementModal(true);
                    setShowMenuDropdown(false);
                }
            });
        }

        return (
            <div
                className={styles.userMenuResults}
                style={{ position: 'absolute', top: y + 'px', left: x + 'px' }}
            >
                {menuItems.map((item, index) => (
                    <div
                        key={index}
                        className={styles.userMenuItem}
                        onClick={item.onClick}
                    >
                        {item.label}
                    </div>
                ))}
            </div>
        );
    };

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                menuButtonRef.current &&
                !menuButtonRef.current.contains(event.target as Node) &&
                !document.querySelector(`.${styles.userMenuResults}`)?.contains(event.target as Node)
            ) {
                setShowMenuDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    return (
        <div className={styles.User}>
            {user?.userDetails?.realname ? (
                <div className={styles.userdata}>
                    <div className={styles.realname}>{user.userDetails?.realname}</div>
                    <button
                        className={styles.menubutton}
                        ref={menuButtonRef}
                        onClick={() => setShowMenuDropdown(!showMenuDropdown)}
                        aria-haspopup="true"
                        aria-expanded={showMenuDropdown}
                    >
                        <GenericIconButton
                            icon='regular/chevron-double-down.svg'
                            hoverEnabled={false}
                            width='8px'
                            height='8px'
                        />
                    </button>
                    {showMenuDropdown && renderUserMenu()}
                </div>
            ) : (
                <>
                    <button className={styles.loginButton} onClick={() => setShowLoginModal(true)}>
                        Login
                    </button>
                    {showLoginModal && (
                        <div className={styles.popupoverlay}>
                            <div className={styles.popup} role="dialog" aria-modal="true" aria-labelledby="login-title">
                                <div className={styles.header}>
                                    <h2 id="login-title">Login</h2>
                                    <button
                                        className={styles.closeButton}
                                        onClick={() => { setShowLoginModal(false); setLoginError(null); }}
                                        aria-label="Close login modal"
                                    >
                                        &times;
                                    </button>
                                </div>
                                <div className={styles.main}>
                                    {loginError && (
                                        <div className={styles.errorMessage}>
                                            {loginError}
                                        </div>
                                    )}
                                    <form onSubmit={handleLogin} className={styles.loginForm}>
                                        <div className={styles.formGroup}>
                                            <label htmlFor="email">Email</label>
                                            <input
                                                type="text"
                                                id="email"
                                                placeholder="Email"
                                                value={email}
                                                onChange={(e) => setEmail(e.target.value)}
                                                required
                                            />
                                        </div>
                                        <div className={styles.formGroup}>
                                            <label htmlFor="password">Password</label>
                                            <input
                                                type="password"
                                                id="password"
                                                placeholder="Password"
                                                value={password}
                                                onChange={(e) => setPassword(e.target.value)}
                                                required
                                            />
                                        </div>
                                        <div className={styles.footer}>
                                            <button
                                                className={`${genericStyles.GenericButton} ${styles.loginSubmitButton}`}
                                                type="submit"
                                            >
                                                Login
                                            </button>
                                            <button
                                                className={`${genericStyles.GenericButton} ${styles.createAccountButton}`}
                                                type="button"
                                                onClick={() => {
                                                    setShowLoginModal(false);
                                                    setShowCreateAccountModal(true);
                                                }}
                                            >
                                                Create Account
                                            </button>
                                            <button
                                                className={`${genericStyles.GenericButton} ${styles.cancelButton}`}
                                                type="button"
                                                onClick={() => { setShowLoginModal(false); setLoginError(null); }}
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </div>
                    )}
                </>
            )}
            <div className={`${styles.message} ${messageVisible ? styles.fadeIn : styles.fadeOut}`}>
                {message}
            </div>

            {showAlbumEditorModal && (
                <div className={styles.albumEditor}>
                    <AlbumEditor onClose={() => { setShowAlbumEditorModal(false) }} />
                </div>
            )}

            {showUserManagementModal && (
                <div className={styles.userManagement}>
                    <UserManagement onClose={() => { setShowUserManagementModal(false) }} />
                </div>
            )}

            {showCreateAccountModal && (
                <CreateAccount onClose={() => { setShowCreateAccountModal(false) }} />
            )}
        </div>
    );
}

export default User;
